import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wifi, WifiOff, X } from "lucide-react";
import type { CardView, GamePlayer } from "../../types/protocol";
import { CardBackMark } from "./CardBackMark";

export interface OpponentInspectorProps {
  player: GamePlayer;
  active: boolean;
  onClose: () => void;
}

const valueLabel = (value: number) => (value < 0 ? `−${Math.abs(value)}` : String(value));

const valueTone = (value: number | null) => {
  if (value === null) return "hidden";
  if (value < 0) return "negative";
  if (value === 0) return "zero";
  if (value <= 4) return "low";
  if (value <= 8) return "mid";
  return "high";
};

const cardLabel = (card: CardView, index: number) => {
  if (card.state === "removed") return `Carte ${index + 1}, retirée`;
  if (card.state === "hidden") return `Carte ${index + 1}, face cachée`;
  return card.value === null
    ? `Carte ${index + 1}, valeur indisponible`
    : `Carte ${index + 1}, valeur ${valueLabel(card.value)}`;
};

const avatarStyle = (seat: number) =>
  ({ "--opponent-inspector-hue": `${(seat * 57 + 242) % 360}` } as CSSProperties);

export const OpponentInspector = ({ player, active, onClose }: OpponentInspectorProps) => {
  const reducedMotion = useReducedMotion();
  const titleId = useId();
  const statusId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /**
   * Relevé au rendu : le focus se pose sur la croix dès le commit, donc avant
   * tout effet. Mesuré dans l'effet, on ne mémorisait que la croix elle-même et
   * la fermeture rendait la main au `body` — un joueur au clavier repartait du
   * haut de la page au lieu de retrouver le plateau qu'il consultait.
   */
  const [previouslyFocused] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCloseRef.current();
    };

    // La prise de focus vit dans l'effet plutôt que sur un `autoFocus` : celui-ci
    // ne joue qu'à l'insertion du nœud, et la restauration ci-dessous le laisserait
    // sans focus dès que React remonte l'effet.
    closeButtonRef.current?.focus();
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      // Le joueur a pu quitter la table pendant la consultation : son plateau
      // n'est alors plus dans le document et ne peut plus rien recevoir.
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [previouslyFocused]);

  const keepFocusInDialog = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab") return;
    event.preventDefault();
    closeButtonRef.current?.focus();
  };

  return (
    <motion.div
      className="opponent-inspector-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.18 }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && event.button === 0) onClose();
      }}
    >
      <motion.section
        className={`opponent-inspector-dialog${active ? " opponent-inspector-dialog--active" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={statusId}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.97 }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : { type: "spring", stiffness: 260, damping: 26 }
        }
        onKeyDown={keepFocusInDialog}
      >
        <header className="opponent-inspector-header">
          <span
            className="opponent-inspector-avatar"
            style={avatarStyle(player.seat)}
            aria-hidden="true"
          >
            {player.name.slice(0, 1).toUpperCase()}
          </span>

          <div className="opponent-inspector-identity">
            <span className="opponent-inspector-kicker">Grille adverse</span>
            <h2 id={titleId}>{player.name}</h2>
          </div>

          <button
            ref={closeButtonRef}
            className="opponent-inspector-close"
            type="button"
            onClick={onClose}
            aria-label={`Fermer la grille de ${player.name}`}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="opponent-inspector-meta" id={statusId}>
          <span
            className={`opponent-inspector-presence opponent-inspector-presence--${player.connected ? "online" : "away"}`}
          >
            {player.connected ? (
              <Wifi size={14} aria-hidden="true" />
            ) : (
              <WifiOff size={14} aria-hidden="true" />
            )}
            {player.connected ? "En ligne" : "Absent"}
          </span>

          {active && (
            <span className="opponent-inspector-turn">
              <i aria-hidden="true" />
              À son tour
            </span>
          )}

          <span className="opponent-inspector-score">
            <small>Total</small>
            <strong>{player.totalScore}</strong>
            <span>pts</span>
          </span>

          {player.roundScore !== null && (
            <span className="opponent-inspector-round-score">
              Manche <strong>{player.roundScore}</strong> pts
            </span>
          )}
        </div>

        <ul
          className="opponent-inspector-grid"
          aria-label={`Grille de ${player.name}, douze cartes`}
        >
          {player.grid.map((card, index) => {
            const revealed = card.state === "revealed";
            const displayedValue = revealed && card.value !== null ? valueLabel(card.value) : null;

            return (
              <motion.li
                className={`opponent-inspector-card opponent-inspector-card--${card.state} opponent-inspector-card--tone-${valueTone(card.value)}`}
                key={index}
                aria-label={cardLabel(card, index)}
                initial={reducedMotion ? false : { opacity: 0, y: 10, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={
                  reducedMotion
                    ? { duration: 0 }
                    : {
                        type: "spring",
                        stiffness: 300,
                        damping: 24,
                        delay: 0.04 + index * 0.018,
                      }
                }
              >
                {card.state === "removed" ? (
                  <span className="opponent-inspector-card__removed" aria-hidden="true">
                    <span><i /><i /></span>
                    <small>Retirée</small>
                  </span>
                ) : card.state === "hidden" ? (
                  <span className="opponent-inspector-card__back" aria-hidden="true">
                    <CardBackMark />
                    <small>Cachée</small>
                  </span>
                ) : (
                  <span className="opponent-inspector-card__front" aria-hidden="true">
                    <small>{displayedValue ?? "—"}</small>
                    <strong>{displayedValue ?? "—"}</strong>
                    <span className="opponent-inspector-card__accent"><i /><i /><i /></span>
                    <small>{displayedValue ?? "—"}</small>
                  </span>
                )}
              </motion.li>
            );
          })}
        </ul>
      </motion.section>
    </motion.div>
  );
};
